/**
 * Country detection utilities (Client-side only)
 * Detects user's country from various sources
 */

import React from 'react';
import { regionFromLocale } from './internationalization/locale';
import { fetchClientGeo } from '@/lib/geo/client-geo';

export interface CountryInfo {
  code: string;
  name: string;
  currency: string;
  source: 'ip' | 'browser' | 'cookie' | 'manual' | 'default';
}

/**
 * Get country from IP address using free geolocation API
 * (Client-side compatible)
 */
export async function getCountryFromIP(): Promise<CountryInfo | null> {
  try {
    console.log('🌍 IP detection: Starting...');
    
    // Using ipapi.co free tier (1000 requests/day)
    const response = await fetch('https://ipapi.co/json/', {
      headers: {
        'Accept': 'application/json'
      }
    });
    
    console.log('🌍 IP detection: Response status:', response.status);
    
    if (!response.ok) {
      throw new Error(`IP lookup failed: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('🌍 IP detection: Response data:', data);
    
    if (data.country_code) {
      const result = {
        code: data.country_code,
        name: data.country_name || getCountryName(data.country_code),
        currency: data.currency || getCurrencyForCountry(data.country_code),
        source: 'ip' as const
      };
      console.log('🌍 IP detection: Final result:', result);
      return result;
    } else {
      console.log('🌍 IP detection: No country_code in response');
    }
  } catch (error) {
    console.error('🌍 Failed to detect country from IP:', error);
  }
  
  return null;
}

/**
 * Get country from browser locale
 */
export function getCountryFromBrowser(): CountryInfo | null {
  if (typeof window === 'undefined') return null;
  
  try {
    console.log('🌍 Browser detection: Starting...');
    
    // Get browser locale
    const locale = navigator.language || (navigator as any).userLanguage;
    console.log('🌍 Browser locale:', locale);
    
    if (locale) {
      const countryCode = regionFromLocale(locale);
      console.log('🌍 Locale region:', countryCode);
      
      if (countryCode) {
        console.log('🌍 Country code from locale:', countryCode);
        
        const result = {
          code: countryCode,
          name: getCountryName(countryCode),
          currency: getCurrencyForCountry(countryCode),
          source: 'browser' as const
        };
        console.log('🌍 Locale-based detection result:', result);
        return result;
      }
    }
    
    // Try timezone-based detection
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('🌍 Browser timezone:', timezone);
    
    const country = getCountryFromTimezone(timezone);
    if (country) {
      console.log('🌍 Timezone-based detection result:', country);
      return {
        ...country,
        source: 'browser' as const
      };
    }
    
    console.log('🌍 Browser detection: No result found');
  } catch (error) {
    console.error('🌍 Failed to detect country from browser:', error);
  }
  
  return null;
}

/**
 * Get country from timezone
 */
function getCountryFromTimezone(timezone: string): CountryInfo | null {
  // Common timezone to country mappings
  const timezoneMap: Record<string, string> = {
    'America/New_York': 'US',
    'America/Chicago': 'US',
    'America/Los_Angeles': 'US',
    'America/Toronto': 'CA',
    'America/Mexico_City': 'MX',
    'America/Sao_Paulo': 'BR',
    'Europe/London': 'GB',
    'Europe/Paris': 'FR',
    'Europe/Berlin': 'DE',
    'Europe/Rome': 'IT',
    'Europe/Madrid': 'ES',
    'Asia/Tokyo': 'JP',
    'Asia/Shanghai': 'CN',
    'Asia/Seoul': 'KR',
    'Asia/Singapore': 'SG',
    'Asia/Kolkata': 'IN',
    'Australia/Sydney': 'AU',
    'Pacific/Auckland': 'NZ'
  };
  
  const countryCode = timezoneMap[timezone];
  if (countryCode) {
    return {
      code: countryCode,
      name: getCountryName(countryCode),
      currency: getCurrencyForCountry(countryCode),
      source: 'browser'
    };
  }
  
  return null;
}

/**
 * Get country name from code
 */
export function getCountryName(code: string): string {
  const countries: Record<string, string> = {
    US: 'United States',
    CA: 'Canada',
    MX: 'Mexico',
    BR: 'Brazil',
    GB: 'United Kingdom',
    DE: 'Germany',
    FR: 'France',
    IT: 'Italy',
    ES: 'Spain',
    NL: 'Netherlands',
    BE: 'Belgium',
    AT: 'Austria',
    CH: 'Switzerland',
    SE: 'Sweden',
    NO: 'Norway',
    DK: 'Denmark',
    FI: 'Finland',
    PL: 'Poland',
    CZ: 'Czech Republic',
    AU: 'Australia',
    NZ: 'New Zealand',
    JP: 'Japan',
    CN: 'China',
    KR: 'South Korea',
    SG: 'Singapore',
    IN: 'India',
    // Gulf countries
    AE: 'United Arab Emirates',
    SA: 'Saudi Arabia',
    KW: 'Kuwait',
    QA: 'Qatar',
    BH: 'Bahrain',
    OM: 'Oman',
    // Others
    ZA: 'South Africa',
    IL: 'Israel',
    IE: 'Ireland',
    PT: 'Portugal',
    GR: 'Greece',
    HU: 'Hungary',
    RO: 'Romania'
  };
  
  return countries[code] || code;
}

/**
 * Get currency for country
 */
export function getCurrencyForCountry(code: string): string {
  const currencies: Record<string, string> = {
    US: 'USD', CA: 'CAD', MX: 'MXN', BR: 'BRL',
    GB: 'GBP', DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR',
    NL: 'EUR', BE: 'EUR', AT: 'EUR', CH: 'CHF', SE: 'SEK',
    NO: 'NOK', DK: 'DKK', FI: 'EUR', PL: 'PLN', CZ: 'CZK',
    AU: 'AUD', NZ: 'NZD', JP: 'JPY', CN: 'CNY', KR: 'KRW',
    SG: 'SGD', IN: 'INR', 
    // Gulf countries
    AE: 'AED', SA: 'SAR', KW: 'KWD', QA: 'QAR', BH: 'BHD', OM: 'OMR',
    // Others
    ZA: 'ZAR', IL: 'ILS', IE: 'EUR', PT: 'EUR', GR: 'EUR',
    HU: 'HUF', RO: 'RON', HR: 'EUR', BG: 'BGN', LT: 'EUR',
    LV: 'EUR', EE: 'EUR', SI: 'EUR', SK: 'EUR', MT: 'EUR',
    CY: 'EUR', LU: 'EUR'
  };
  
  return currencies[code] || 'USD';
}

/**
 * React hook for country detection
 */
// Cache expiration: 7 days in milliseconds
const COUNTRY_CACHE_EXPIRATION = 7 * 24 * 60 * 60 * 1000;

/**
 * Standalone function to detect country code (for non-React contexts)
 * Returns just the country code string or null
 */
export async function detectCountry(): Promise<string | null> {
  // Check localStorage first
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('detectedCountry');
    const cacheTimestamp = localStorage.getItem('detectedCountryTimestamp');

    if (stored && cacheTimestamp) {
      const cacheAge = Date.now() - parseInt(cacheTimestamp, 10);
      if (cacheAge < COUNTRY_CACHE_EXPIRATION) {
        try {
          const parsed = JSON.parse(stored);
          return parsed.code;
        } catch (e) {
          // Continue to detection
        }
      }
    }
  }

  // Try browser detection first (faster)
  const browserCountry = getCountryFromBrowser();
  if (browserCountry) {
    return browserCountry.code;
  }

  // Fall back to IP detection
  const ipCountry = await getCountryFromIP();
  if (ipCountry) {
    return ipCountry.code;
  }

  return null;
}

type CountryState = { country: CountryInfo | null; loading: boolean };

function persistCountry(country: CountryInfo) {
  if (typeof window === 'undefined') return;
  localStorage.setItem('detectedCountry', JSON.stringify(country));
  localStorage.setItem('detectedCountryTimestamp', String(Date.now()));
}

/** The country Cloudflare saw for this request, via our own /api/geo (null in dev or when unknown). */
async function getCountryFromServer(): Promise<CountryInfo | null> {
  if (typeof window === 'undefined') return null;
  try {
    // Shared with lib/pricing/story-page-counts-client.ts: one /api/geo request per page load.
    const country = (await fetchClientGeo())?.country;
    if (typeof country === 'string' && /^[A-Z]{2}$/.test(country)) {
      return {
        code: country,
        name: getCountryName(country),
        currency: getCurrencyForCountry(country),
        source: 'ip',
      };
    }
  } catch {
    // fall through to the locale / ipapi.co lookups
  }
  return null;
}

/** Cached choice first, then Cloudflare country (/api/geo), then browser locale, then IP lookup, then US. */
async function detectCountryInfo(): Promise<CountryInfo> {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('detectedCountry');
    const cacheTimestamp = localStorage.getItem('detectedCountryTimestamp');
    if (stored && !cacheTimestamp) {
      localStorage.removeItem('detectedCountry');
    } else if (stored && cacheTimestamp) {
      if (Date.now() - parseInt(cacheTimestamp, 10) <= COUNTRY_CACHE_EXPIRATION) {
        try {
          return JSON.parse(stored);
        } catch {
          // invalid cache: detect again
        }
      } else {
        localStorage.removeItem('detectedCountry');
        localStorage.removeItem('detectedCountryTimestamp');
      }
    }
  }

  const detected = (await getCountryFromServer()) || getCountryFromBrowser() || (await getCountryFromIP()) || {
    code: 'US',
    name: 'United States',
    currency: 'USD',
    source: 'default' as const,
  };
  persistCountry(detected);
  return detected;
}

// One country for the whole page, shared by every component that shows a price: detection runs
// once, and a manual choice (the /pricing country selector) moves every price at once.
const UNKNOWN: CountryState = { country: null, loading: true };
let state: CountryState = UNKNOWN;
let detection: Promise<void> | null = null;
let detector: () => Promise<CountryInfo> = detectCountryInfo;
const listeners = new Set<() => void>();

function setState(next: CountryState) {
  state = next;
  listeners.forEach((l) => l());
}

export const countryStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  },
  getSnapshot: () => state,
  getServerSnapshot: () => UNKNOWN,
  ensureDetected(): Promise<void> {
    if (!detection) {
      detection = detector().then((country) => {
        // A manual choice made while detection was running wins.
        if (state.loading) setState({ country, loading: false });
      });
    }
    return detection;
  },
  setCountryCode(code: string) {
    const country: CountryInfo = {
      code,
      name: getCountryName(code),
      currency: getCurrencyForCountry(code),
      source: 'manual',
    };
    persistCountry(country);
    setState({ country, loading: false });
  },
  setDetectorForTests(fn: () => Promise<CountryInfo>) { detector = fn; },
  resetForTests() {
    state = UNKNOWN;
    detection = null;
    detector = detectCountryInfo;
    listeners.clear();
  },
};

export function useCountryDetection() {
  const { country, loading } = React.useSyncExternalStore(
    countryStore.subscribe,
    countryStore.getSnapshot,
    countryStore.getServerSnapshot,
  );
  React.useEffect(() => { countryStore.ensureDetected(); }, []);
  return { country, loading, setCountryCode: countryStore.setCountryCode };
}
